/**
 * plugins/index.ts
 *
 * Registers app plugins inside the vite-ssg setup callback. Pinia and
 * Vuetify are created fresh per render so server prerenders never share
 * state or meta across pages. The router and the unhead (head) instance
 * are created and installed by vite-ssg itself, so they are NOT registered
 * here — useHead()/useSeo() resolve the head via injection.
 */

import { createPinia } from "pinia";

import "@mdi/font/css/materialdesignicons.css";
import { createVuetify } from "vuetify";
import "vuetify/styles";

import type { ViteSSGContext } from "vite-ssg";

export function registerPlugins({ app, initialState }: ViteSSGContext) {
  const pinia = createPinia();

  // Components and directives are auto-imported on use by
  // vite-plugin-vuetify (configured in vite.config.mts). `ssr: true` lets
  // Vuetify render correctly on the server and hydrate without mismatches.
  //
  // NOTE: Vuetify cannot deliver its own theme CSS in this app — see
  // useVuetifyThemeStyles() in App.vue, which injects it instead.
  const vuetify = createVuetify({ ssr: true });

  // NOTE: Vueform is registered client-only in main.ts — it touches
  // `document` at import time and is not used on any prerendered page.
  app.use(pinia).use(vuetify);

  // Serialize Pinia state into the prerendered HTML on the server and
  // restore it on the client so hydration matches the static markup.
  if (import.meta.env.SSR) {
    initialState.pinia = pinia.state.value;
  } else if (initialState.pinia) {
    pinia.state.value = initialState.pinia as typeof pinia.state.value;
  }
}
